import {classList, listen} from 'nuffjs';
import {ucFirst} from './string';
import {isString} from './types';

/**
 * Convert a css time expression in ms
 *
 * @param {string} value value expressed in string eventually ending with a unit like 'ms' or 's'
 * @returns {number} value expressed in ms
 * @ignore
 */
function toMs(value) {
	const numericValue = parseFloat(value);
	if (isNaN(numericValue)) {
		return 0;
	}
	return value.indexOf('ms') === -1 && value.indexOf('s') > -1 ? numericValue * 1000 : numericValue;
}

/**
 * Extract the transition duration from the element (or pseudoelement) style
 *
 * @param {Element} element the element to check
 * @param {?string} [pseudoElement=null] eventual pseudoelement to use as a source of computed style
 * @param {boolean} [includeDelay=true] take into account the transition delay
 * @param {boolean} [seconds=true] return the value in seconds or milliseconds
 * @returns {number} the resulting duration expressed in seconds or milliseconds
 */
function getTransitionDuration(element, pseudoElement = null, includeDelay = true, seconds = true) {
	const style = getComputedStyle(element, pseudoElement);
	let duration = 0;
	let delay = 0;
	const durations = style['transition-duration'].split(/\s*,\s/);
	for (const value of durations) {
		duration = Math.max(duration, toMs(value));
	}
	if (includeDelay) {
		const delays = style['transition-delay'].split(/\s*,\s/);
		for (const value of delays) {
			delay = Math.max(delay, toMs(value));
		}
	}
	const total = duration + delay;
	return seconds ? total / 1000 : total;
}

/**
 * Get a promise that resolves when an element transition (previously triggered by some change, like
 * a property or class toggle) is completed.
 *
 * @param {Element} element the element that is having a transition
 * @param {?string} [pseudoElement=null] eventual pseudoelement to use to calculate a transition
 * duration as a timeout fallback in case the event does not dispatch for some reason
 * @param {number} [errorMargin=100] milliseconds to wait after the expected transition duration
 * before forcing the end if the event has not been dispatched
 * @returns {Promise<element>} resolves with the element itself
 */
function onTransitionEnd(element, pseudoElement = null, errorMargin = 100) {
	return new Promise((resolve) => {
		let raised = false;
		let listener = null;
		let timeout = null;
		const duration = getTransitionDuration(element, pseudoElement, true, false) + errorMargin;
		if (duration > 0) {
			listener = listen(element, 'transitionend', (event) => {
				// in some cases the visibility transitionend triggers before the overall duration
				if (event.target === element && event.propertyName !== 'visibility') {
					listener.unlisten();
					if (!raised) {
						raised = true;
						if (timeout) {
							clearTimeout(timeout);
						}
						resolve(element);
					}
				}
			});
			timeout = setTimeout(() => {
				if (!raised) {
					raised = true;
					if (listener) {
						listener.unlisten();
					}
					resolve(element);
				}
			}, duration);
		} else {
			resolve(element);
		}
	});
}

/**
 * Get a promise that resolves when a CSS animation ends (when the corresponding event has been
 * intercepted)
 *
 * @param {Element} element
 * @returns {Promise} resolve when the animationend event has been intercepted
 */
function onAnimationEnd(element) {
	return listen(element, 'animationend');
}

/**
 * @classdesc There are situation when a simple css transition is not possible in order to achieve a
 * specific result. A typical case is when an element is having "display: none", since in that case
 * setting the display to a different value and transitioning something (like opacity) at the same
 * time does not work. In order to solve this we use a mechanism that we called three state
 * transition:
 *
 * 1. we set a first class, then wait a frame (typically here the display property is set to a value
 *    != none)
 * 2. we set a second class, then we wait for a transition end (this class actually toggle the
 *    transition)
 * 3. we set a third class, in case we needs to set / clear something at the end.
 *
 * When reversing the transition, the classes are removed with the same method but in reverse order.
 *
 * Usually we pass a single class name and the three names are derived from that. Let's say the
 *  class is *active*, the derived 3 classes will be: *beforeActive*, *duringActive*, *active*.
 *
 *
 * @class ThreeStateTransition
 * @version 1.0.0
 * @path
 *
 */
class ThreeStateTransition {
	constructor(element, animatedElement = null) {
		this.element = element;
		this.animatedElement = animatedElement === null ? element : animatedElement;
	}

	/**
	 * Add the classes
	 *
	 * @param {string|object} name a string or an object containing the properties before, during
	 * and after.
	 * @param {boolean} [immediate=false] true in the (remote) case in which we want all 3 classes
	 * to be applied at the same time without waiting the proper timing in between.
	 * @returns {Promise} resolve when the whole 3-state transition is completed
	 * @memberof ThreeStateTransition
	 */
	add(name, immediate = false) {
		return this.applyTransition(name, true, immediate);
	}

	/**
	 * Remove the classes
	 *
	 * @param {string|object} name a string or an object containing the properties before, during
	 * and after.
	 * @param {boolean} [immediate=false] true in the (remote) case in which we want all 3 classes
	 * to be removed at the same time without waiting the proper timing in between.
	 * @returns {Promise} resolve when the whole 3-state transition is completed
	 * @memberof ThreeStateTransition
	 */
	remove(name, immediate = false) {
		return this.applyTransition(name, false, immediate);
	}

	/**
	 * Used internally to add / remove the classes
	 *
	 * @param {string|object} name a string or an object containing the properties before, during
	 * and after.
	 * @param {boolean} add true = add, false = remove
	 * @param {boolean} [immediate=false] true in the (remote) case in which we want all 3 classes
	 * to be removed at the same time without waiting the proper timing in between.
	 * @returns {Promise} resolve when the whole 3-state transition is completed
	 * @protected
	 * @ignore
	 */
	applyTransition(name, add, immediate = false) {
		let beforeName;
		let afterName;
		let duringName;
		if (isString(name)) {
			afterName = name;
			beforeName = 'before' + ucFirst(afterName);
			duringName = 'during' + ucFirst(afterName);
		} else {
			beforeName = name.before;
			afterName = name.after;
			duringName = name.during;
		}
		if (!add) {
			const swap = afterName;
			afterName = beforeName;
			beforeName = swap;
		}

		const cl = classList(this.element);
		if (immediate) {
			cl.toggle(beforeName, add);
			cl.toggle(duringName, add);
			cl.toggle(afterName, add);
			return Promise.resolve(this.element);
		}
		return new Promise((resolve) => {
			requestAnimationFrame(() => {
				cl.toggle(beforeName, add);
				requestAnimationFrame(() => {
					cl.toggle(duringName, add);
					onTransitionEnd(this.animatedElement).then(() => {
						requestAnimationFrame(() => cl.toggle(afterName));
						resolve(this.element);
					});
				});
			});
		});
	}
}

/**
 * create a ThreeStateTransition object
 *
 * @param {Element} element classes will be applied to this element
 * @param {(Element|null)} [animatedElement=null] it is possible to specify a different element to wait
 * for the transitionEnd event. When null, element will be used
 * @returns {ThreeStateTransition} an object implementing the three states transition
 */
function threeStateTransition(element, animatedElement = null) {
	return new ThreeStateTransition(element, animatedElement);
}

export {
	getTransitionDuration,
	onTransitionEnd,
	onAnimationEnd,
	threeStateTransition,
	ThreeStateTransition,
};
